import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Alert, Box, Button, Collapse, Typography } from '@mui/material';
import { useState } from 'react';
import { ONLINE_CART_TOKENS } from './style';

function DetailParagraph({ detail }) {
    const colonIndex = detail.indexOf(':');
    if (colonIndex === -1) {
        return <Typography sx={{ fontSize: '0.875rem', lineHeight: 1.6, color: ONLINE_CART_TOKENS.textMutedSoft }}>{detail}</Typography>;
    }

    const heading = detail.slice(0, colonIndex + 1);
    const body = detail.slice(colonIndex + 1).trim();

    return (
        <Typography sx={{ fontSize: '0.875rem', lineHeight: 1.6, color: ONLINE_CART_TOKENS.textMutedSoft }}>
            <Typography component="span" sx={{ fontWeight: 700, color: ONLINE_CART_TOKENS.textHeadingSoft }}>
                {heading}
            </Typography>{' '}
            {body}
        </Typography>
    );
}

function ExpandableWarning({ warning }) {
    const [open, setOpen] = useState(false);

    return (
        <Box sx={{ mt: 3 }}>
            <Button
                variant="text"
                onClick={() => setOpen((prev) => !prev)}
                sx={{
                    p: 0,
                    minWidth: 0,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    color: ONLINE_CART_TOKENS.primary,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                    '&:hover': {
                        backgroundColor: 'transparent',
                        textDecoration: 'underline',
                    },
                }}
            >
                <InfoOutlinedIcon sx={{ fontSize: 18 }} />
                <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: ONLINE_CART_TOKENS.primary }}>
                    {warning.preview} {warning.actionText || 'Learn more'}
                </Typography>
                {open ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
            </Button>

            <Collapse in={open}>
                <Box
                    sx={{
                        mt: 1.5,
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: ONLINE_CART_TOKENS.surface,
                        border: `1px solid ${ONLINE_CART_TOKENS.borderSoft}`,
                    }}
                >
                    {(warning.details || []).map((detail, index) => (
                        <Box key={detail} sx={{ mb: index === (warning.details || []).length - 1 ? 0 : 1.25 }}>
                            <DetailParagraph detail={detail} />
                        </Box>
                    ))}
                </Box>
            </Collapse>
        </Box>
    );
}

function StaticWarning({ warning }) {
    return (
        <Alert
            icon={false}
            severity="info"
            sx={{
                mt: 3,
                borderRadius: 2,
                backgroundColor: ONLINE_CART_TOKENS.lightInfoBg,
                border: `1px solid ${ONLINE_CART_TOKENS.lightInfoBorder}`,
                color: ONLINE_CART_TOKENS.infoText,
                '& .MuiAlert-message': {
                    width: '100%',
                },
            }}
        >
            <Typography component="span" sx={{ fontWeight: '600', color: ONLINE_CART_TOKENS.textHeadingSoft, fontSize: '14px' }}>
                {warning.title}
            </Typography>
            <Typography component="span" sx={{ ml: 0.75, fontSize: '14px' }}>
                {`- ${warning.text}`}
            </Typography>
        </Alert>
    );
}

export function DisplayNote({ note, sx={} }) {
    if(!note){
        return null
    }
    return (
        <Box
            sx={{
                mt: 2.25,
                borderRadius: 2,
                bgcolor: ONLINE_CART_TOKENS.neutralBgSoft,
                p: 2,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.25,
                color: ONLINE_CART_TOKENS.textSupport,

                ...(typeof sx === 'object' ? sx : {}),
            }}
        >
            {note.icon === 'info' && <InfoOutlinedIcon sx={{ fontSize: 18, mt: 0.1 }} />}
            <Typography sx={{ fontSize: '0.75rem', color: ONLINE_CART_TOKENS.textSupport, lineHeight: 1.6 }}>
                <Typography component="span" sx={{ fontSize: 'inherit', fontWeight: 700, color: ONLINE_CART_TOKENS.textSupport }}>
                    {note.title || 'Note:'}
                </Typography>{' '}
                {note.text || ''}
            </Typography>
        </Box>
    );
}

export default function WarningBox({ warning }) {
    if (!warning) {
        return null;
    }

    if (warning.variant === 'expandable') {
        return <ExpandableWarning warning={warning} />;
    }

    if(warning.variant === 'note'){
        return <DisplayNote note={warning} />;
    }

    return <StaticWarning warning={warning} />;
}
